'use client';

import { formatDate } from '@/lib/config';
import type { WishlistItem as WishlistItemType } from '@home-service/contracts';
import Image from 'next/image';
import { useState } from 'react';
import styles from './WishlistItem.module.css';

interface Props {
    item: WishlistItemType;
    index: number;
}

export default function WishlistItem({ item, index }: Props) {
    const images = item.images ?? [];
    const links = item.links ?? [];
    const shortId = item.id?.slice(0, 6) ?? '???';
    // State for gallery navigation and error handling
    const [imageError, setImageError] = useState(false);
    const [imgIndex, setImgIndex] = useState(0);

    // Safety checks
    const hasImages = images.length > 0;
    const currentImage = hasImages ? images[imgIndex] : null;

    // Handlers
    const nextImage = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (hasImages) {
            setImgIndex((prev) => (prev + 1) % images.length);
            setImageError(false); // Reset error on change
        }
    };

    const prevImage = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (hasImages) {
            setImgIndex((prev) => (prev - 1 + images.length) % images.length);
            setImageError(false);
        }
    };

    return (
        <article id={`item-${item.id}`} className={styles.card}>
            {/* Left Side: Image Gallery */}
            <div className={styles.imageArea} onClick={nextImage} style={{ cursor: hasImages && images.length > 1 ? 'pointer' : 'default' }}>
                <div className={styles.crtOverlay} />
                {currentImage && !imageError ? (
                    <Image
                        key={currentImage.url} // Key forces re-render for transition
                        src={currentImage.url}
                        alt={item.title}
                        width={240}
                        height={240}
                        className={styles.image}
                        unoptimized
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <span className={styles.imagePlaceholder}>📦</span>
                )}

                {/* Gallery Controls (Only if > 1 image) */}
                {images.length > 1 && (
                    <div className={styles.galleryOverlay}>
                        <button className={styles.galleryBtn} onClick={prevImage}>
                            [&lt;]
                        </button>
                        <span className={styles.galleryCounter}>
                            IMG: {String(imgIndex + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}
                        </span>
                        <button className={styles.galleryBtn} onClick={nextImage}>
                            [&gt;]
                        </button>
                    </div>
                )}
            </div>

            {/* Right Side: Content */}
            <div className={styles.content}>
                {/* Header: Title & Price */}
                <div className={styles.headerRow}>
                    <h2 className={styles.title}>{item.title}</h2>
                    {item.price && <div className={styles.price}>{item.price.toLocaleString('ru-RU')} ₽</div>}
                </div>

                {/* Meta Row: Status, ID, Tags (if any) */}
                <div className={styles.metaRow}>
                    <div className={styles.metaTag}>
                        <span className={`${styles.dot} ${styles.dotActive}`}></span>
                        <span>Item [{index + 1}]</span>
                    </div>
                    <div className={styles.metaTag}>ID: #{shortId}</div>
                    {images.length > 0 && <div className={styles.metaTag}>photos: {images.length}</div>}
                    {item.tags && item.tags.length > 0 && (
                        <div className={styles.tagsGroup}>
                            {item.tags.map((tag) => (
                                <span
                                    key={tag.name}
                                    className={styles.tag}
                                    style={{
                                        color: `var(--${tag.color})`,
                                        borderColor: `var(--${tag.color})`,
                                    }}
                                >
                                    {tag.name}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Description */}
                {item.description && <div className={styles.description}>{item.description}</div>}

                {/* Note */}
                {item.note && (
                    <div className={styles.noteBox}>
                        <span className={styles.noteLabel}>Note</span>
                        {item.note}
                    </div>
                )}

                {/* Footer: Actions */}
                <div className={styles.actions}>
                    <div className={styles.linksGroup}>
                        {links.map((link, i) => (
                            <a
                                key={link.id || i}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={link.isPrimary ? styles.btnPrimary : styles.btnSecondary}
                            >
                                {link.title || 'Открыть ссылку'}
                            </a>
                        ))}
                    </div>

                    <span className={styles.addedDate}>Added: {formatDate(item.createdAt)}</span>
                </div>
            </div>
        </article>
    );
}
